/**
 * Dialog component for editing a single-dimension KPI formula.
 * Wraps the CalculatedKPIBuilder with an optional DimensionTree panel for browsing and inserting DimSum nodes.
 */
import './SingleDimensionDialog.scss';

import {
  DimensionPickerDialogContent,
  DimensionPickerProvider,
  useDimensionNodeResolver,
} from '@amzn/gft-dimension-picker';
import { DefaultTreeItemProps, GFTDialog } from '@amzn/gft-frontend-commons';
import { useDeckContext } from '@context/DeckContext';
import { CalculatedKPIBuilderHandle } from '@deckManagement/types/CalculatedKPIBuilder.types';
import { Alert, Grid, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { isNil } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import CalculatedKPIBuilder from './CalculatedKPIBuilder';

export interface SingleDimensionDialogProps {
  open: boolean;
  dimensionName: string;
  dimensionLabel: string;
  formula: string;
  onFormulaChange?: (newFormula: string) => void;
  onClose?: () => void;
}

const SingleDimensionDialog = (props: SingleDimensionDialogProps) => {
  const { dimensionLabel, dimensionName, formula, onClose, onFormulaChange, open } = props;
  const [localFormula, setLocalFormula] = useState<string>('');
  const [modifiedFormula, setModifiedFormula] = useState<string>('');
  const [modifiedFormulaValid, setModifiedFormulaValid] = useState<boolean>(true);
  const [modifiedFormulaError, setModifiedFormulaError] = useState<string | null>(null);
  const [showDimensionTree, setShowDimensionTree] = useState<boolean>(false);
  const [defaultRootNode, setDefaultRootNode] = useState<string | undefined>();
  const [insertInProgress, setInsertInProgress] = useState<string | null>(null);

  const { resolveNode } = useDimensionNodeResolver();

  const calculatedKpiRef = useRef<CalculatedKPIBuilderHandle>(null);

  const { rootNodes } = useDeckContext();

  /**
   * Resolves the default root node ID for the DimensionTree based on the current dimension name.
   */
  useEffect(() => {
    if (dimensionName && rootNodes) {
      const rootNodeList = rootNodes[dimensionName] || [];
      setDefaultRootNode(rootNodeList[0]?.nodeID);
    }
  }, [rootNodes, dimensionName]);

  /**
   * Update local state when formula changes
   */
  useEffect(() => {
    setLocalFormula(formula);
    setModifiedFormula(formula);
  }, [formula]);

  /**
   * Handles resolving a DimSum node by ID and inserting it into the KPI formula via the builder ref.
   * @param nodeId The DimSum node ID to resolve and insert.
   */
  const handleInsertButtonClick = useCallback(
    (nodeId: string) => {
      setInsertInProgress(nodeId);
      resolveNode(nodeId, true)
        .then((nodeData) => {
          calculatedKpiRef.current?.insertNodeIntoFormula(nodeData);
        })
        .finally(() => {
          setInsertInProgress(null);
        });
    },
    [resolveNode],
  );

  /**
   * Handles rendering the custom Item Label Text for the DefaultTreeItem for GFTTreeView
   * @param itemProps the internal DefaultTreeItemProps which were given to the component for rendering.
   */
  const renderTreeItemLabelText = useCallback<NonNullable<DefaultTreeItemProps['renderTreeItemLabelText']>>(
    (itemProps) => {
      const { node, labelClasses, isSelected } = itemProps;
      const { id, name, description } = node;
      const labelText = description ? `${name} - ${description}` : name;
      return (
        <Stack direction='row' useFlexGap className='custom-tree-item-label'>
          <Typography variant='body2' className={labelClasses}>
            {labelText}
          </Typography>
          {isSelected && (
            <Button
              size='small'
              type='button'
              variant='contained'
              disabled={!isNil(insertInProgress)}
              onClick={(e) => {
                e.stopPropagation();
                if (isNil(insertInProgress)) {
                  handleInsertButtonClick(id);
                }
              }}
            >
              {insertInProgress ? 'Wait...' : 'Insert'}
            </Button>
          )}
        </Stack>
      );
    },
    [handleInsertButtonClick, insertInProgress],
  );

  /**
   * Handles updating the local formula when it changes.
   */
  const handleFormulaChange = useCallback((newFormula: string, isValid: boolean, validationError: string | null) => {
    setModifiedFormulaValid(isValid);
    setModifiedFormulaError(validationError);
    setModifiedFormula(newFormula);
  }, []);

  /**
   * Handles closing the dialog WITHOUT confirming bubbling formula changes up to the parent component.
   */
  const handleCloseDialog = useCallback(() => {
    onClose?.();
  }, [onClose]);

  /**
   * Handles the dialog Confirm button click.
   */
  const handleConfirm = useCallback(() => {
    if (modifiedFormula !== localFormula) {
      onFormulaChange?.(modifiedFormula);
    }

    onClose?.();
  }, [localFormula, modifiedFormula, onClose, onFormulaChange]);

  /**
   * Handles rendering the DimensionTree for browsing and inserting DimSum Nodes into the formula
   */
  const renderDimensionTree = useCallback(() => {
    return (
      <DimensionPickerProvider dimension={dimensionName} selectedRootNode={defaultRootNode}>
        <DimensionPickerDialogContent
          dimensionTreeProps={{ renderTreeItemLabelText }}
          dimensionSearchResultsTreeProps={{ renderTreeItemLabelText }}
        />
      </DimensionPickerProvider>
    );
  }, [defaultRootNode, dimensionName, renderTreeItemLabelText]);

  return (
    <GFTDialog
      className={`calculated-kpi-single-dimension-dialog ${showDimensionTree ? 'with-dimension-tree' : ''}`}
      open={open}
      onClose={handleCloseDialog}
      title='KPI Formula Builder'
      actions={
        <>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirm} variant='contained' color='primary' disabled={!modifiedFormulaValid}>
            Save
          </Button>
        </>
      }
    >
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid size={{ lg: showDimensionTree ? 6 : 12, md: 12 }}>
          <Stack spacing={2}>
            <CalculatedKPIBuilder
              key='kpi-builder'
              ref={calculatedKpiRef}
              initialFormula={localFormula}
              label={dimensionLabel}
              dimensionName={dimensionName}
              onFormulaChange={handleFormulaChange}
            />

            <Alert variant='outlined' severity={modifiedFormulaValid ? 'success' : 'error'} key='formula-status'>
              {modifiedFormulaValid ? 'Formula is valid.' : modifiedFormulaError || 'Invalid Formula'}
            </Alert>
            <Button onClick={() => setShowDimensionTree((prev) => !prev)}>
              {showDimensionTree ? 'Hide' : 'Show'} Dimension Tree
            </Button>
          </Stack>
        </Grid>
        {showDimensionTree && <Grid size={{ lg: 6, md: 12 }}>{renderDimensionTree()}</Grid>}
      </Grid>
    </GFTDialog>
  );
};

export default SingleDimensionDialog;
